import React, { useState, useContext } from 'react';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { Link, useNavigate } from 'react-router-dom';
import { PageAbsolutePath } from '../../../consts/Common';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Links from '../../common/Widgets/Links';
import { SiteContext } from '../../../context/SiteContext';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const AlternateEligibility = (): JSX.Element => {
  const navigate = useNavigate();
  const { site } = useContext(SiteContext);

  const { brand } = site;

  const brandPhoneNumber = brand.brandPhoneNumbers.quotesAndRenewalsNumber;

  const chevronLeftComponent = <FontAwesomeIcon icon={faChevronLeft} />;

  const goBack = (): void => {
    navigate(PageAbsolutePath.Insurance.Landing);
  };

  const [expanded, setExpanded] = useState<string | false>('');

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  return (
    <div>
      <Links
        id='back-link'
        title=''
        items={[{ to: PageAbsolutePath.Insurance.Landing, text: 'Back' }]}
        icon={chevronLeftComponent}
      />
      <h4>Your Driveaway Insurance and 12 Month Quote application</h4>
      <p>
        Please ensure you have the following to hand, as you will need these to
        complete your application:
      </p>
      <ul>
        <li>{`Your ${brand.brandDisplayName} vehicle order number`}</li>
        <li>Your vehicle registration</li>
        <li>Your vehicle delivery date</li>
        <li>Your order confirmation email</li>
      </ul>
      <p>
        Before applying for your cover we need to make sure you are eligible.
        Please make sure you read the following carefully and check you meet all
        of our eligibility criteria below:
      </p>
      <div>
        <Accordion
          disableGutters
          expanded={expanded === 'accordion-1'}
          onChange={handleAccordionChange('accordion-1')}
          className='accordion-simple'>
          <AccordionSummary
            sx={{
              minHeight: '0px',
              '.MuiAccordionSummary-content': { mt: '0.5rem', mb: 0, ml: 0 }
            }}
            expandIcon={
              expanded === 'accordion-1' ? (
                <RemoveIcon style={{ color: '#000' }} />
              ) : (
                <AddIcon style={{ color: '#000' }} />
              )
            }
            aria-controls='customerPaneld-content'
            id='eligibility-accordion-2'>
            <h4>Eligibility for Driveaway Insurance</h4>
          </AccordionSummary>
          <AccordionDetails>
            <p>
              By proceeding, you confirm the following details about you and all
              drivers:
            </p>
            <ol>
              <li>I have not yet taken delivery of the vehicle.</li>
              <li>
                All drivers:
                <ul>
                  <li>
                    are aged 21 to 79 years (age criteria may vary by vehicle
                    variant).
                  </li>
                  <li>
                    have a full UK driving licence held for at least 1 year and
                    have been permanent UK residents for the last 3 years.
                  </li>
                  <li>
                    have no unspent criminal convictions and have had no more
                    than 2 fault claims in the last 3 years.
                  </li>
                  <li>
                    have no driving bans in the last 5 years and have no greater
                    than 9 penalty points on their licence.
                  </li>
                  <li>
                    have not been refused motor insurance, had any policy
                    cancelled or voided, or had special terms imposed.
                  </li>
                  <li>
                    agree to provide us with DVLA access to validate their
                    driving licence in the event of a claim.
                  </li>
                </ul>
              </li>
              <li>
                The vehicle to be insured:
                <ul>
                  <li>
                    has a minimum value of £2,000 and is not more than 8 years
                    old.
                  </li>
                  <li>
                    has not been modified from the manufacturer’s original
                    specification.
                  </li>
                  <li>
                    will not be used for any business purposes, carriage of
                    goods or passengers for hire and reward, racing, pacemaking,
                    speed testing, competitions, rallies or trails, track days,
                    whether on a road, track or an off-road event, commercial
                    travelling, scrap waste or use for any purpose in relation
                    to the motor trade.
                  </li>
                  <li>
                    will only be used for Social, Domestic and Pleasure purposes
                    including commuting to one permanent place of business.
                  </li>
                </ul>
              </li>
              <li>
                Tracker requirements - for cover to be effective a Thatcham
                category S5 tracking device must be fitted and active for:
                <ul>
                  <li>pure electric vehicles valued over £60,000</li>
                  <li>petrol and diesel vehicles valued over £65,000</li>
                </ul>
              </li>
              <li>{`The acceptance criteria for non-${brand.brandDisplayName} vehicles vary by make and model. Several specific brands are excluded.`}</li>
              <li>
                All cover is subject to the terms and conditions provided with
                the policy, the
                <Link
                  id='lnkPolicyDocumentInText'
                  className='in-text'
                  to={PageAbsolutePath.Insurance.PolicyDocuments}>
                  Policy Document
                </Link>
                and the
                <Link
                  id='lnkTermsOfBusinessInText'
                  className='in-text'
                  to={PageAbsolutePath.Insurance.BusinessTermsAncillaryPage}>
                  Terms of Business.
                </Link>
              </li>
            </ol>
            <p>What if the applicant(s) doesn't meet the above criteria?</p>
            <p>
              Please proceed with the online application regardless to ensure
              they can be issued with their Free £250 Excess Voucher, which can
              be activated in your back office. (This is subject to them
              qualifying)
            </p>
          </AccordionDetails>
        </Accordion>
      </div>
      <div>
        <Accordion
          disableGutters
          expanded={expanded === 'accordion-2'}
          onChange={handleAccordionChange('accordion-2')}
          className='accordion-simple'>
          <AccordionSummary
            sx={{
              minHeight: '0px',
              '.MuiAccordionSummary-content': { mt: '0.5rem', mb: 0, ml: 0 }
            }}
            expandIcon={
              expanded === 'accordion-2' ? (
                <RemoveIcon style={{ color: '#000' }} />
              ) : (
                <AddIcon style={{ color: '#000' }} />
              )
            }
            aria-controls='customerPaneld-content'
            id='eligibility-accordion-3'>
            <h4>Eligibility for Quote</h4>
          </AccordionSummary>
          <AccordionDetails>
            <p>
              By proceeding, you confirm the following details about you and all
              drivers:
            </p>
            <ul>
              <li>All drivers are aged 17 to 90 years</li>
              <li>{`All drivers have been permanent UK residents for the last 3 years. If less than 3 years, call ${brandPhoneNumber} for a 12 Month quote.`}</li>
              <li>
                All drivers have a full UK/EU or provisional driving licence.
              </li>
              <li>
                All drivers have zero or a maximum of one county court judgement
                (CCJ) in the last 3 years.
              </li>
              <li>
                No drivers have been disqualified from driving in the last 5
                years.
              </li>
              <li>
                I understand that all cover is subject to the terms and
                conditions provided with the policy, the
                <Link
                  id='lnkPolicyDocumentInText'
                  className='in-text'
                  to={PageAbsolutePath.Insurance.PolicyDocuments}>
                  Policy Document
                </Link>
                and the
                <Link
                  id='lnkTermsOfBusinessInText'
                  className='in-text'
                  to={PageAbsolutePath.Insurance.BusinessTermsAncillaryPage}>
                  Terms of Business.
                </Link>
              </li>
            </ul>
          </AccordionDetails>
        </Accordion>
      </div>
      <div className='flex-row form-nav'>
        <ButtonBack
          name='btnBack'
          text='Back'
          onClick={goBack}
          additionalClassNames='btn-back'
        />
        <ButtonContinue
          name='btnNext'
          text='Next'
          onClick={() => navigate(PageAbsolutePath.Insurance.OrderVerification)}
          additionalClassNames='btn-continue'
        />
      </div>
    </div>
  );
};

export default AlternateEligibility;
